/**
 * DELEGATE — the personalised suggestion starters read moved to
 * `@heidi/evidence` at the `evidence/source` carve.
 * `GET /chat/personalized-suggestions` still returns recent-work–informed
 * evidence suggestion starters (GRO-2896), experiment-gated on the backend, over
 * the same `scribeHttpV2` instance.
 *
 * Does not refetch on mount, window focus, or reconnect — the package hook holds
 * those three flags plus `staleTime: Infinity`, unchanged. Call `refetch()` or
 * invalidate {@link PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY} when you need a
 * fresh payload (sessionless Evidence awareness invalidates after each completed
 * impression).
 *
 * What stays here is the app-resolved gate — the pre-move `!!token && enabled`,
 * with `enabled` still defaulting to `false` at this signature, where the
 * caller-facing contract lives.
 *
 * The gate also carries the SURFACE term. `GET /v2/chat/personalized-suggestions`
 * stays in ml-scribe's cloud-only chat router, so on an on-prem build there is
 * no such route. The one caller (`useSessionlessEvidenceAwarenessPopover`)
 * drives the read through `enabled` and `invalidateQueries`, and an invalidation
 * does not refetch a disabled query, so this term closes it. It would NOT stop
 * an explicit `refetch()` — a caller that adds one must read the const itself.
 *
 * `PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY` is DECLARED in
 * `@heidi/evidence/contracts` and re-exported here name-intact — as the ARRAY it
 * has always been, which is what both consumers pass straight to `queryKey` and
 * `invalidateQueries`.
 */
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { isChatPersonalizedSuggestionsApiAvailable } from '@/utils/backendSurfaces';
import { useQueryPersonalizedEvidenceSuggestions as useQueryPersonalizedEvidenceSuggestionsCore } from '@heidi/evidence/data';

export { PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY } from '@heidi/evidence/contracts';
export type { PersonalizedEvidenceSuggestionsResponse } from '@heidi/evidence/contracts';

interface UseQueryPersonalizedEvidenceSuggestionsOptions {
  /**
   * When true (and the user has a token), the query runs automatically while enabled.
   * Default false: call `refetch()` when you want data.
   */
  enabled?: boolean;
}

export function useQueryPersonalizedEvidenceSuggestions({
  enabled = false,
}: UseQueryPersonalizedEvidenceSuggestionsOptions = {}) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQueryPersonalizedEvidenceSuggestionsCore({
    enabled: isChatPersonalizedSuggestionsApiAvailable && isAuthenticated && enabled,
  });
}
